import React from 'react'

export const KpiCard: React.FC<{
  label: string
  value: React.ReactNode
  hint?: string
  icon?: React.ReactNode
  emphasize?: boolean
  muted?: boolean
}> = ({ label, value, hint, icon, emphasize, muted }) => (
  <div className={`rounded-xl border p-5 ${muted ? 'bg-gray-50 border-gray-100' : 'bg-white border-gray-200'} ${emphasize ? 'ring-1 ring-indigo-100 shadow-sm' : ''}`}>
    <div className="flex items-start justify-between gap-3">
      <p className={`text-sm font-medium ${muted ? 'text-gray-500' : 'text-gray-600'}`}>{label}</p>
      {icon ? <div className={`${muted ? 'text-gray-400' : 'text-blue-600'} opacity-80`}>{icon}</div> : null}
    </div>
    <div className={`mt-3 font-bold tracking-tight ${muted ? 'text-2xl text-gray-700' : emphasize ? 'text-4xl text-gray-900' : 'text-3xl text-gray-900'}`}>
      {value}
    </div>
    {hint ? <p className="mt-2 text-xs text-gray-500 leading-snug">{hint}</p> : null}
  </div>
)

export const InsightCard: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/90 to-white p-5 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-2">{title}</p>
    <p className="text-sm text-gray-800 leading-relaxed">{body}</p>
  </div>
)

export const UsageBadge: React.FC<{ status: string; label: string }> = ({ status, label }) => {
  const colors: Record<string, string> = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-orange-100 text-orange-800',
    none: 'bg-gray-100 text-gray-600',
  }
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors.none}`}>
      {label}
    </span>
  )
}

export const EmptyState: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div className="p-10 text-center">
    <p className="text-base font-medium text-gray-800">{title}</p>
    {hint ? <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">{hint}</p> : null}
  </div>
)

export const DemoBanner: React.FC<{ label: string }> = ({ label }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
    {label}
  </div>
)

export const formatDate = (v?: string | null) => {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleString()
  } catch {
    return String(v)
  }
}

export const IconUsers = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m4-4a4 4 0 110-8 4 4 0 010 8zm6 0a3 3 0 110-6 3 3 0 010 6z" /></svg>
)
export const IconTeacher = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422A12.083 12.083 0 0112 21.5a12.083 12.083 0 01-6.16-10.922L12 14z" /></svg>
)
export const IconClass = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1" /></svg>
)
export const IconActivity = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
)
export const IconChat = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
)
